const productSubscriptionsV2 = class extends HTMLElement {
  constructor() {
    super()
    this._onVariantChange = (event) => {
      const form = this.closest('form')
      if (!form || !event.detail?.variant) { return }
      if (!this.eventBelongsToForm(event, form)) { return }
      this.variantChanged(event.detail.variant)
    }
    this._onOptionInputChange = (event) => {
      const input = event.target
      if (!input?.matches?.('input[data-option-position]')) { return }

      const picker = input.closest('variant-picker')
      if (!picker) { return }

      const form = this.closest('form')
      const formId = form?.getAttribute('id')
      const pickerFormId = picker.getAttribute('form-id')
      if (formId && pickerFormId && pickerFormId !== formId) { return }

      const variantId = this.resolveVariantIdFromPicker(picker)
      if (variantId) { this.applyVariantFromLocalData(variantId) }
    }
    this._onProductRerender = (event) => {
      const form = this.closest('form')
      if (!form || event.target !== form) { return }

      this.cacheElements()
      const variantId = Number(form.querySelector('[name="id"]')?.value)
      if (variantId) { this.applyVariantFromLocalData(variantId) }
      this.syncHeaderPrice()
    }
  }

  connectedCallback() {
    if (this._initialized) { return }
    this._initialized = true

    const form = this.closest('form')

    this.cacheElements()
    this.variantId = Number(this.dataset.selectedVariantId)
    this.activeType = [...this.subOptions].find(option => option.checked)?.value || 'subscribe'
    this.sellingPlanInput = form?.querySelector('[data-selling-plan-input]')
    this.quantityInput = form?.querySelector('[data-quantity-input]')

    this.optionsJSON = this.parseJsonScript(this.queryInside('[data-options-json]'))
    this.servingsJSON = this.parseJsonScript(this.queryInside('[data-variant-servings-json]'))
    this.variantPrices = this.parseJsonScript(this.queryInside('[data-variant-prices-json]'))

    window.addEventListener('variant:change', this._onVariantChange)
    document.addEventListener('change', this._onOptionInputChange, true)

    if (form) {
      form.addEventListener('product:rerender', this._onProductRerender)
      this._onFormSubmit = () => {
        this.syncPurchaseType(this.isOneTimeSelected() ? 'one-time' : 'subscribe')
      }
      form.addEventListener('submit', this._onFormSubmit, true)
    }

    this.subOptions.forEach((option) => {
      option.addEventListener('change', () => {
        this.updateType(option.value)
      })
    })

    if (this.select) {
      this.select.addEventListener('change', () => {
        this.updateSellingPlan()
      })
      this.select.addEventListener('mousedown', () => {
        this.selectOptionBox(this.select.closest('[data-subscription-box]'))
      })
    }

    this.quantityButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault()
        event.stopPropagation()
        this.selectOptionBox(button.closest('[data-subscription-box]'))
        this.updateQuantity(button.dataset.quantityOption)
      })
    })

    this.bindPurchaseCtas()

    this.currentVariant = {
      id: this.variantId,
      price: this.getCurrentVariantPrice(),
      compare_at_price: this.getCurrentVariantCompareAtPrice()
    }

    this.syncPurchaseType(this.activeType)
    this.initPerServingPrices()
  }

  disconnectedCallback() {
    window.removeEventListener('variant:change', this._onVariantChange)
    document.removeEventListener('change', this._onOptionInputChange, true)
    const form = this.closest('form')
    form?.removeEventListener('product:rerender', this._onProductRerender)
    form?.removeEventListener('submit', this._onFormSubmit, true)
    this._initialized = false
  }

  eventBelongsToForm(event, form) {
    const formAttrId = form.getAttribute('id')
    const incoming = event.detail.formId

    if (!incoming) { return true }
    if (incoming === form || incoming === formAttrId) { return true }
    if (incoming instanceof HTMLElement) {
      if (incoming === form.id) { return true }
      const incomingForm = incoming.form || incoming.closest?.('form')
      return !incomingForm || incomingForm === form
    }

    return String(incoming) === String(formAttrId)
  }

  cacheElements() {
    this.subOptions = this.queryInsideAll('[data-subscription-option]')
    this.select = this.queryInside('[data-selling-plan-select]')
    this.subContent = this.queryInside('[data-subscribe-content]')
    this.buyPrice = this.queryInside('[data-buy-price]')
    this.subPrice = this.queryInside('[data-sub-price]')
    this.buyPerServing = this.queryInside('[data-buy-per-serving]')
    this.subPerServing = this.queryInside('[data-sub-per-serving]')
    this.quantityButtons = this.queryInsideAll('[data-quantity-option]')
  }

  isOneTimeSelected() {
    return Boolean(this.queryInside('[data-subscription-option][value="one-time"]')?.checked)
  }

  syncPurchaseType(type) {
    this.activeType = type
    this.updatePurchaseUI()

    if (type === 'one-time') {
      this.disableSellingPlan()
    } else {
      this.resetQuantity()
      this.enableSellingPlan(this.select?.value)
    }

    this.syncHeaderPrice()
  }

  queryInside(selector) {
    return this.querySelector(selector) || this.parentElement?.querySelector(selector) || null
  }

  queryInsideAll(selector) {
    const local = this.querySelectorAll(selector)
    if (local.length) { return local }
    return this.parentElement?.querySelectorAll(selector) || local
  }

  parseJsonScript(script) {
    if (!script) { return {} }
    try {
      return JSON.parse(script.textContent)
    } catch {
      return {}
    }
  }

  initPerServingPrices() {
    this.updateAllPerServingPrices()
  }

  getSelectedPlanPrice() {
    const selectedOption = this.select?.querySelector('option:checked') || this.select?.querySelector('option')
    if (selectedOption?.dataset.variantPrice) {
      return Number(selectedOption.dataset.variantPrice)
    }

    const plans = this.optionsJSON?.[this.variantId] || this.optionsJSON?.[String(this.variantId)] || []
    if (plans[0]?.price) { return Number(plans[0].price) }

    return null
  }

  updateAllPerServingPrices() {
    this.buyPerServing = this.queryInside('[data-buy-per-serving]')
    this.subPerServing = this.queryInside('[data-sub-per-serving]')

    const buyPrice = this.currentVariant?.price ?? this.getCurrentVariantPrice()
    this.updatePerServing(this.buyPerServing, buyPrice)

    const subPrice = this.getSelectedPlanPrice() ?? buyPrice
    this.updatePerServing(this.subPerServing, subPrice)
  }

  getCurrentVariantPrice() {
    const priceEl = this.buyPrice?.querySelector('.product-subscriptions__option-price--bold')
    if (!priceEl) { return 0 }

    const match = priceEl.textContent.replace(/[^0-9.]/g, '')
    return Math.round(parseFloat(match) * 100)
  }

  getCurrentVariantCompareAtPrice() {
    const compareEl = this.buyPrice?.querySelector('s.text-subdued')
    if (!compareEl) { return null }

    const match = compareEl.textContent.replace(/[^0-9.]/g, '')
    const value = Math.round(parseFloat(match) * 100)
    return Number.isFinite(value) ? value : null
  }

  getHeaderPriceList() {
    const rerender = this.closest('product-rerender')
    if (rerender) {
      const quickBuyPrice = rerender.querySelector('.quick-buy-drawer__price price-list')
      if (quickBuyPrice) { return quickBuyPrice }
    }

    const scope = this.closest('.shopify-section--main-product-v2')
    return scope?.querySelector('.product-info-v2__price price-list') || null
  }

  syncHeaderPrice() {
    if (this.activeType === 'one-time') {
      const price = this.currentVariant?.price ?? this.getCurrentVariantPrice()
      const compareAt = this.currentVariant?.compare_at_price ?? this.getCurrentVariantCompareAtPrice()
      this.updateHeaderPrice(price, compareAt)
      return
    }

    const selectedOption = this.select?.querySelector('option:checked') || this.select?.querySelector('option')
    if (!selectedOption) { return }

    this.updateHeaderPrice(
      Number(selectedOption.dataset.variantPrice),
      Number(selectedOption.dataset.variantCompareAtPrice)
    )
  }

  updateHeaderPrice(price, comparePrice) {
    const priceList = this.getHeaderPriceList()
    const salePrice = priceList?.querySelector('sale-price')
    if (!salePrice || !price) { return }

    const srOnly = salePrice.querySelector('.sr-only')
    salePrice.replaceChildren(...(srOnly ? [srOnly] : []))
    salePrice.append(document.createTextNode(this.intToMoney(price)))

    const compareAt = priceList.querySelector('compare-at-price')
    const onSale = comparePrice && comparePrice > price

    if (onSale) {
      const compareEl = compareAt || document.createElement('compare-at-price')
      if (!compareAt) {
        compareEl.className = 'line-through'
        priceList.appendChild(compareEl)
      }

      const compareSrOnly = compareEl.querySelector('.sr-only')
      compareEl.replaceChildren(...(compareSrOnly ? [compareSrOnly] : []))
      compareEl.append(document.createTextNode(this.intToMoney(comparePrice)))
      salePrice.classList.add('bold')
    } else {
      compareAt?.remove()
      salePrice.classList.remove('bold')
    }
  }

  selectOptionBox(box) {
    if (!box) { return }

    const radio = box.querySelector('[data-subscription-option]')
    if (!radio || radio.checked) { return }

    radio.checked = true
    radio.dispatchEvent(new Event('change', { bubbles: true }))
  }

  bindPurchaseCtas() {
    const layout = this.closest('.product-subscriptions-v2__purchase-layout')
    if (!layout) { return }

    const oneTimeRadio = this.querySelector('[data-subscription-option][value="one-time"]')
    const oneTimeCta = layout.querySelector('.product-subscriptions-v2__cta--one-time')

    oneTimeCta?.addEventListener('mousedown', () => {
      if (oneTimeRadio && !oneTimeRadio.checked) {
        oneTimeRadio.checked = true
        oneTimeRadio.dispatchEvent(new Event('change', { bubbles: true }))
      }
    })
  }

  updatePurchaseUI() {
    const isOneTime = this.activeType === 'one-time'

    this.subOptions.forEach((option) => {
      option.closest('[data-subscription-box]')?.classList.toggle('is-active', option.checked)
    })

    if (this.subContent) {
      this.subContent.classList.toggle('is-inactive', isOneTime)
    }
  }

  updateType(value) {
    this.syncPurchaseType(value)
  }

  updateQuantity(value) {
    if (!this.quantityInput) { return }

    this.quantityInput.value = value
    this.quantityButtons.forEach((button) => {
      button.classList.toggle('is-selected', button.dataset.quantityOption === value)
    })
  }

  resetQuantity() {
    const minQty = this.quantityButtons[0]?.dataset.quantityOption || '1'
    this.updateQuantity(minQty)
  }

  disableSellingPlan() {
    const form = this.closest('form')
    if (!form) { return }

    // Prefer data attribute so we still find the field after removing its name.
    const planInputs = form.querySelectorAll(
      '[data-selling-plan-input], [name="selling_plan"], [name^="selling_plan_"]'
    )

    planInputs.forEach((input) => {
      input.disabled = true
      input.value = ''
      if (input.matches('[data-selling-plan-input], [name="selling_plan"]')) {
        input.removeAttribute('name')
      }
    })

    if (this.sellingPlanInput) {
      this.sellingPlanInput.disabled = true
      this.sellingPlanInput.value = ''
      this.sellingPlanInput.removeAttribute('name')
    }
  }

  enableSellingPlan(planId) {
    if (!planId) { return }

    const form = this.closest('form')
    if (!form) { return }

    const planInputs = this.sellingPlanInput
      ? [this.sellingPlanInput]
      : [...form.querySelectorAll('[data-selling-plan-input]')]

    planInputs.forEach((input) => {
      input.disabled = false
      input.setAttribute('name', 'selling_plan')
      input.value = planId
    })

    form.querySelectorAll('[name^="selling_plan_"]').forEach((input) => {
      const isSelectedPlan = input.name === `selling_plan_${planId}`
      input.disabled = !isSelectedPlan
      if (isSelectedPlan && !input.value) {
        input.value = 'subsave'
      }
    })
  }

  refreshSubscriptionDisplay() {
    if (!this.select) { return }

    const options = [...this.select.querySelectorAll('option')]
    const firstAvailableOption = options.find(option => !option.hidden && !option.disabled)
    if (!firstAvailableOption) { return }

    this.select.value = firstAvailableOption.value

    if (this.subPrice) {
      this.subPrice.innerHTML = this.buildPrice(
        Number(firstAvailableOption.dataset.variantPrice),
        Number(firstAvailableOption.dataset.variantCompareAtPrice)
      )
    }

    this.updatePerServing(this.subPerServing, Number(firstAvailableOption.dataset.variantPrice))
  }

  updateSellingPlan() {
    if (!this.select) { return }

    const selectedOption = [...this.select.querySelectorAll('option')].find(option => option.value === this.select.value)
    if (!selectedOption) { return }

    if (this.subPrice) {
      this.subPrice.innerHTML = this.buildPrice(
        Number(selectedOption.dataset.variantPrice),
        Number(selectedOption.dataset.variantCompareAtPrice)
      )
    }
    this.updatePerServing(this.subPerServing, Number(selectedOption.dataset.variantPrice))

    if (this.activeType === 'one-time') { return }

    this.enableSellingPlan(this.select.value)
    this.syncHeaderPrice()
  }

  resolveVariantIdFromPicker(picker) {
    const checked = [...(picker?.querySelectorAll('input[data-option-position]:checked') || [])]
    if (!checked.length) { return null }

    let ids = null
    for (const input of checked) {
      const next = (input.dataset.variants || '').split(',').map((value) => value.trim()).filter(Boolean)
      if (!next.length) { continue }
      ids = ids ? ids.filter((id) => next.includes(id)) : next
    }

    const match = ids?.find((id) => this.optionsJSON?.[id] || this.optionsJSON?.[String(id)] || this.variantPrices?.[id] || this.variantPrices?.[String(id)])
    return match ? Number(match) : (ids?.[0] ? Number(ids[0]) : null)
  }

  getVariantPriceData(variantId) {
    const stored = this.variantPrices?.[variantId] || this.variantPrices?.[String(variantId)] || {}
    const plans = this.optionsJSON?.[variantId] || this.optionsJSON?.[String(variantId)] || []
    const plan = plans[0] || {}

    const price = Number(stored.price ?? plan.compare_price ?? plan.price)
    const compareAt = stored.compare_at_price ?? plan.compare_price ?? null

    return {
      id: Number(variantId),
      price,
      compare_at_price: compareAt
    }
  }

  applyVariantFromLocalData(variantId) {
    const id = Number(variantId)
    if (!id) { return }

    this.cacheElements()
    this.variantId = id
    this.currentVariant = this.getVariantPriceData(id)

    const servings = this.getServings()
    if (servings) { this.dataset.servings = String(servings) }

    this.buildOptions()

    if (this.buyPrice && this.currentVariant.price) {
      this.buyPrice.innerHTML = this.buildPrice(this.currentVariant.price, this.currentVariant.compare_at_price)
    }

    this.refreshSubscriptionDisplay()
    this.updateAllPerServingPrices()
    this.syncPurchaseType(this.activeType)
  }

  variantChanged(variant) {
    if (!variant?.id) { return }

    const local = this.getVariantPriceData(variant.id)
    this.applyVariantFromLocalData(variant.id)
    this.currentVariant = {
      id: variant.id,
      price: variant.price ?? local.price,
      compare_at_price: variant.compare_at_price ?? local.compare_at_price
    }

    if (this.buyPrice) {
      this.buyPrice.innerHTML = this.buildPrice(this.currentVariant.price, this.currentVariant.compare_at_price)
    }
    this.updateAllPerServingPrices()
    this.syncHeaderPrice()
  }

  buildOptions() {
    if (!this.select) { return }

    const data = this.optionsJSON[this.variantId] || this.optionsJSON[String(this.variantId)] || []
    const optionEls = data.map((option) => {
      return `
        <option
          value="${option.id}"
          data-variant-price="${option.price}"
          data-variant-compare-at-price="${option.compare_price}"
        >
          ${option.name}
        </option>
      `
    })

    this.select.innerHTML = optionEls.join('')
  }

  updatePerServing(element, price) {
    if (!element) { return }

    const servings = this.getServings()
    if (!servings || !price) {
      element.hidden = true
      return
    }

    element.hidden = false
    element.textContent = `${this.intToMoney(price / servings)}/serving`
  }

  getServings() {
    const fromJson = this.extractServings(
      this.servingsJSON?.[this.variantId] ?? this.servingsJSON?.[String(this.variantId)]
    )
    if (fromJson) { return fromJson }

    const scope = this.closest('.shopify-section')
      || this.closest('product-rerender')
      || this.closest('.quick-buy-drawer')
      || document
    const selectedMeta = scope.querySelector(
      '.size-card:has(> input:checked) .size-card__meta, .size-card.is-selected .size-card__meta'
    )
    const fromCard = this.extractServings(selectedMeta?.textContent)
    if (fromCard) { return fromCard }

    return this.extractServings(this.dataset.servings)
  }

  extractServings(value) {
    if (!value) { return null }

    const match = String(value).match(/(\d+(?:\.\d+)?)/)
    return match ? Number(match[1]) : null
  }

  buildPrice(price, comparePrice) {
    if (!comparePrice || price >= comparePrice) {
      return `
        <span class="product-subscriptions__option-price--bold">${this.intToMoney(price)}</span>
      `
    }

    return `
      <span class="product-subscriptions__option-price--bold">${this.intToMoney(price)}</span>
      <s class="text-subdued">${this.intToMoney(comparePrice)}</s>
    `
  }

  intToMoney(price) {
    return `$${(price / 100).toFixed(2)}`
  }
}

if (typeof window.customElements.get('product-subscriptions-v2') === 'undefined') {
  window.customElements.define('product-subscriptions-v2', productSubscriptionsV2)
}

function initProductSubscriptionsV2(root = document) {
  root.querySelectorAll('product-subscriptions-v2').forEach((el) => {
    if (!el._initialized && typeof el.connectedCallback === 'function') {
      el.connectedCallback()
    }
  })
}

function initProductV2QuantityButtons(root = document) {
  root.querySelectorAll('[data-quantity-buttons]').forEach((group) => {
    if (group.closest('product-subscriptions-v2')) { return }
    if (group.dataset.initialized === 'true') { return }
    group.dataset.initialized = 'true'

    const form = group.closest('form')
    const input = form?.querySelector('[data-quantity-input]') || group.parentElement?.querySelector('[data-quantity-input]')
    const buttons = group.querySelectorAll('[data-quantity-option]')

    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        if (!input) { return }
        input.value = button.dataset.quantityOption
        buttons.forEach((item) => {
          item.classList.toggle('is-selected', item === button)
        })
      })
    })
  })
}

document.addEventListener('DOMContentLoaded', () => {
  initProductV2QuantityButtons()
  initProductSubscriptionsV2()
})
document.addEventListener('shopify:section:load', (event) => {
  initProductV2QuantityButtons(event.target)
  initProductSubscriptionsV2(event.target)
})
